import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { ChevronRight, Users, Target, TrendingUp, Heart, Award, Globe, ArrowRight, GraduationCap, Shield, BookOpen, Home, Sparkles, Star, Flower } from "lucide-react";

function Sanyuka() {
  const [isVisible, setIsVisible] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activePhase, setActivePhase] = useState(0);

  const heroImages = [
    "img/sanyuka/8-min.jpg",
    "img/sanyuka/2-min.jpg",
    "img/sanyuka/3-min.jpg"
  ];

  const galleryImages = [
    "img/sanyuka/2-min.jpg",
    "img/sanyuka/3-min.jpg", 
    "img/sanyuka/6-min.jpg",
    "img/sanyuka/8-min.jpg",
    "img/sanyuka/11-min.jpg",
    "img/sanyuka/19-min.jpg"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);

    const phaseInterval = setInterval(() => {
      setActivePhase((prev) => (prev + 1) % 4);
    }, 3000);

    return () => {
      observer.disconnect();
      clearInterval(imageInterval);
      clearInterval(phaseInterval);
    };
  }, []);

  const stats = [
    { icon: GraduationCap, number: "60%", label: "At Risk of Dropout", description: "Adolescent girls due to menstrual challenges (UBOS 2016)", color: "from-pink-500 to-rose-500" },
    { icon: Users, number: "12-19", label: "Age Group Targeted", description: "Upper primary & secondary school girls", color: "from-purple-500 to-pink-500" },
    { icon: Globe, number: "4", label: "Districts Covered", description: "Kayunga, Mukono, Jinja, and Wakiso", color: "from-blue-500 to-purple-500" },
    { icon: Heart, number: "5+", label: "Years of Impact", description: "Transforming lives since project inception", color: "from-green-500 to-emerald-500" }
  ];

  const challenges = [
    {
      icon: Shield,
      title: "Lack of Menstrual Products",
      description: "Limited access to proper sanitary products creates barriers to education",
      impact: "School Absenteeism",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: Home,
      title: "Inadequate Facilities",
      description: "Schools lack clean, private spaces for menstrual hygiene management",
      impact: "Reduced Dignity",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: BookOpen,
      title: "Knowledge Gaps",
      description: "Girls lack proper education about their bodies and menstrual health",
      impact: "Shame & Stigma",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Users,
      title: "Community Barriers",
      description: "Cultural taboos and lack of support from families and communities",
      impact: "Educational Dropout",
      color: "from-orange-500 to-red-500"
    }
  ];

  const solutions = [
    {
      phase: 1,
      title: "Education & Awareness",
      description: "Teaching girls about their bodies and proper menstrual hygiene management",
      icon: BookOpen,
      activities: ["Body awareness sessions", "Hygiene education", "Health workshops"],
      color: "from-blue-500 to-purple-500"
    },
    {
      phase: 2,
      title: "Product Distribution",
      description: "Providing free reusable sanitary products to reduce financial barriers",
      icon: Heart,
      activities: ["Reusable pad distribution", "Emergency supplies", "Quality assurance"],
      color: "from-pink-500 to-rose-500"
    },
    {
      phase: 3,
      title: "Infrastructure Improvement",
      description: "Enhancing school facilities for better menstrual hygiene management",
      icon: Home,
      activities: ["Separate girl toilets", "Clean water access", "Privacy installations"],
      color: "from-green-500 to-emerald-500"
    },
    {
      phase: 4,
      title: "Community Engagement",
      description: "Involving parents, teachers, and leaders in supporting girls' education",
      icon: Users,
      activities: ["Parent workshops", "Teacher training", "Community dialogues"],
      color: "from-purple-500 to-pink-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-rose-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-8 sm:pt-20 lg:pt-40 pb-12 sm:pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-32 sm:w-64 h-32 sm:h-64 bg-pink-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-24 sm:w-48 h-24 sm:h-48 bg-purple-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-16 sm:w-32 h-16 sm:h-32 bg-rose-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
          
          {/* Flower pattern */}
          <div className="absolute top-1/3 right-1/4 w-48 sm:w-96 h-48 sm:h-96 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-pink-600 animate-spin" style={{animationDuration: '40s'}}>
              <path d="M50,20 Q60,30 50,40 Q40,30 50,20 M50,60 Q60,70 50,80 Q40,70 50,60 M20,50 Q30,40 40,50 Q30,60 20,50 M60,50 Q70,40 80,50 Q70,60 60,50 M50,50 Q55,45 60,50 Q55,55 50,50 Q45,55 40,50 Q45,45 50,50" fill="currentColor"/>
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Content */}
                  <div 
                    id="hero-content"
                    data-animate
                    className={`mt-14 sm:mt-14 lg:mt-0 text-center lg:text-left transform transition-all duration-1000 ${
                    isVisible['hero-content'] ? 'translate-x-0 opacity-100' : 'lg:-translate-x-full opacity-0'
                    }`}
                  >
                    <div className="inline-flex items-center bg-pink-100 text-pink-800 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                    <Sparkles className="w-3 sm:w-4 h-3 sm:h-4 mr-2" />
                    Girls' Empowerment Initiative
                    </div>
                    
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 leading-tight">
                    <span className="bg-gradient-to-r from-pink-600 via-purple-600 to-rose-600 bg-clip-text text-transparent">
                      Sanyuka Nange
                    </span>
                    <br />
                    <span className="text-gray-800">Menstrual Hygiene</span>
                    <br />
                    <span className="text-gray-800">Management</span>
                    </h1>
                    
                    <div className="bg-gradient-to-r from-pink-50 to-purple-50 border-l-4 border-pink-500 p-4 sm:p-6 rounded-r-xl sm:rounded-r-2xl mb-6 sm:mb-8">
                    <p className="text-base sm:text-lg text-gray-700 font-medium">
                      <span className="font-bold text-pink-800">"Sanyuka Nange"</span> means "Be Happy with Me." 
                      We're breaking down barriers to education by ensuring every girl can manage her 
                      menstrual health with dignity and confidence.
                    </p>
                    </div>
                    
                    <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed mb-6 sm:mb-8">
                    Empowering adolescent girls aged 12-19 across Kayunga, Mukono, Jinja, and Wakiso districts 
                    through comprehensive menstrual hygiene education, product distribution, and community engagement.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                    <button 
                      onClick={() => window.location.href = '/contact-us'}
                      className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold text-base sm:text-lg transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2 w-full sm:w-auto"
                    >
                      Partner With Us <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
                    </button>
                    </div>
                  </div>

                  {/* Image Carousel */}
            <div 
              id="hero-image"
              data-animate
              className={`transform transition-all duration-1000 delay-300 ${
                isVisible['hero-image'] ? 'translate-x-0 opacity-100' : 'lg:translate-x-full opacity-0'
              }`}
            >
              <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                <div className="relative h-64 sm:h-80 md:h-96 lg:h-[500px]">
                  <img
                    src={heroImages[currentImageIndex]}
                    alt="Sanyuka Nange Project"
                    className="w-full h-full object-cover transition-opacity duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  
                  {/* Overlay badge */}
                  <div className="absolute top-4 sm:top-6 left-4 sm:left-6 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 sm:w-5 h-4 sm:h-5 text-pink-600" />
                      <span className="font-semibold text-gray-800 text-sm sm:text-base">Empowering Girls</span>
                    </div>
                  </div>
                </div>
                
                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 sm:py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div 
            id="stats-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['stats-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              The <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Challenge</span> We Address
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Understanding the barriers that prevent girls from staying in school and achieving their potential
            </p>
            <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full mx-auto mt-4 sm:mt-6"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 ${
                    isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-pink-500">
                    <div className={`w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r ${stat.color} rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 mx-auto`}>
                      <Icon className="w-6 sm:w-8 h-6 sm:h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-base sm:text-lg font-semibold text-pink-600 mb-2">{stat.label}</div>
                      <div className="text-xs sm:text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Why We Care Section */}
      <div className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div 
            id="challenges-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['challenges-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              Why We <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">Care</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-4xl mx-auto px-4">
              In many of our communities, girls struggle to manage their periods due to multiple interconnected challenges 
              that threaten their education and future opportunities.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {challenges.map((challenge, index) => {
              const Icon = challenge.icon;
              return (
                <div
                  key={index}
                  id={`challenge-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 ${
                    isVisible[`challenge-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}
                >
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                    <div className={`w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r ${challenge.color} rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6`}>
                      <Icon className="w-6 sm:w-8 h-6 sm:h-8 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">{challenge.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{challenge.description}</p>
                    <div className="inline-flex items-center bg-red-100 text-red-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                      {challenge.impact}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* How We Help - Animated Solution Process */}
      <div className="py-12 sm:py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div 
            id="solutions-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['solutions-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              How We <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Help</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Our comprehensive four-phase approach addresses every aspect of menstrual hygiene management
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              const isActive = activePhase === index;
              return (
                <div
                  key={index}
                  className={`transform transition-all duration-500 ${
                    isActive ? 'scale-105 -translate-y-2' : 'scale-100'
                  }`}
                >
                  <div className={`bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg transition-all duration-500 border-2 ${
                    isActive ? 'border-green-500 shadow-2xl' : 'border-transparent'
                  }`}>
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <div className={`w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r ${solution.color} rounded-xl sm:rounded-2xl flex items-center justify-center`}>
                        <Icon className="w-6 sm:w-8 h-6 sm:h-8 text-white" />
                      </div>
                      <div className={`w-6 sm:w-8 h-6 sm:h-8 rounded-full flex items-center justify-center font-bold text-white text-sm sm:text-base transition-all duration-500 ${
                        isActive ? 'bg-green-500 scale-125' : 'bg-gray-400'
                      }`}>
                        {solution.phase}
                      </div>
                    </div>
                    
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">{solution.title}</h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{solution.description}</p>
                    
                    <div className="space-y-2">
                      {solution.activities.map((activity, actIndex) => (
                        <div key={actIndex} className="flex items-center text-xs sm:text-sm text-gray-600">
                          <div className={`w-2 h-2 rounded-full mr-3 transition-all duration-500 ${
                            isActive ? 'bg-green-500' : 'bg-gray-300'
                          }`}></div>
                          {activity}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div 
              id="results-content"
              data-animate
              className={`transform transition-all duration-1000 ${
                isVisible['results-content'] ? 'translate-x-0 opacity-100' : 'lg:-translate-x-full opacity-0'
              }`}
            >
              <div className="inline-flex items-center bg-green-100 text-green-800 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                <Award className="w-3 sm:w-4 h-3 sm:h-4 mr-2" />
                Proven Results
              </div>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6 sm:mb-8">
                What We've <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Achieved</span>
              </h2>
              
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                <p>
                  Over the past five years, we've witnessed <strong>incredible transformations</strong> in our communities. 
                  Our comprehensive approach has created lasting change in the lives of thousands of girls.
                </p>
                
                <div className="grid grid-cols-1 gap-4 sm:gap-6">
                  <div className="bg-green-50 border-l-4 border-green-500 p-3 sm:p-4 rounded-r-xl sm:rounded-r-2xl">
                    <div className="font-bold text-green-800 mb-2 text-sm sm:text-base">Educational Impact</div>
                    <div className="text-green-700 text-sm sm:text-base">Fewer girls missing school and dropping out due to menstruation</div>
                  </div>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded-r-xl sm:rounded-r-2xl">
                    <div className="font-bold text-blue-800 mb-2 text-sm sm:text-base">Academic Performance</div>
                    <div className="text-blue-700 text-sm sm:text-base">Improved grades and increased school engagement</div>
                  </div>
                </div>
                
                <p>
                  By removing the barriers associated with menstruation, we're empowering girls to stay in school, 
                  excel in their studies, and look forward to brighter futures. Our project isn't just about hygiene—
                  it's about <strong>breaking down barriers to education and creating opportunities</strong>.
                </p>
              </div>
            </div>

            <div 
              id="results-visual"
              data-animate
              className={`transform transition-all duration-1000 delay-300 mt-8 lg:mt-0 ${
                isVisible['results-visual'] ? 'translate-x-0 opacity-100' : 'lg:translate-x-full opacity-0'
              }`}
            >
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-3 sm:space-y-4">
                  <img
                    src="img/sanyuka/2-min.jpg"
                    alt="Girls education"
                    className="w-full h-32 sm:h-48 object-cover rounded-xl sm:rounded-2xl shadow-lg"
                  />
                  <img
                    src="img/sanyuka/6-min.jpg"
                    alt="Community engagement"
                    className="w-full h-24 sm:h-32 object-cover rounded-xl sm:rounded-2xl shadow-lg"
                  />
                </div>
                <div className="space-y-3 sm:space-y-4 pt-4 sm:pt-8">
                  <img
                    src="img/sanyuka/3-min.jpg"
                    alt="Product distribution"
                    className="w-full h-24 sm:h-32 object-cover rounded-xl sm:rounded-2xl shadow-lg"
                  />
                  <img
                    src="img/sanyuka/11-min.jpg"
                    alt="Happy girls"
                    className="w-full h-32 sm:h-48 object-cover rounded-xl sm:rounded-2xl shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Gallery */}
      <div className="py-12 sm:py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div 
            id="gallery-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['gallery-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              Project <span className="bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">Gallery</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 px-4">
              Moments of empowerment, education, and transformation
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                id={`gallery-${index}`}
                data-animate
                className={`group relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 ${
                  isVisible[`gallery-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <img
                  src={image}
                  alt={`Sanyuka project activity ${index + 1}`}
                  className="w-full h-48 sm:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 text-white">
                    <div className="font-semibold text-sm sm:text-base">Sanyuka Nange</div>
                    <div className="text-xs sm:text-sm text-gray-200">Empowering Girls</div>
                  </div>
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                    <Flower className="w-5 sm:w-6 h-5 sm:h-6 text-white/80" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-12 sm:py-20 bg-gradient-to-r from-pink-600 via-purple-600 to-rose-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-base sm:text-lg font-semibold mb-6 sm:mb-8">
              <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 mr-2 sm:mr-3" />
              Join Our Mission to Empower Girls
            </div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 sm:mb-8">
              Be Happy with Us
            </h2>
            <p className="text-base sm:text-xl text-white/90 mb-8 sm:mb-12 max-w-3xl mx-auto px-4">
              Join us in breaking down barriers to education and empowering girls to reach their full potential. 
              Together, we can change the statistic that 60% of girls are at risk of dropping out due to menstrual challenges.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center px-4">
              <button 
                onClick={() => window.location.href = '/contact-us'}
                className="bg-white text-pink-600 px-8 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
              >
                Partner With Us
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Sanyuka;